import { Component, Input } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import greatCircle from '@turf/great-circle';
import type { FeatureCollection, Point } from 'geojson';
import { environment } from 'env/environment';
import { LocationService } from 'app/services/location.service';
import { CommonModule } from '@angular/common';
import { SvgLoaderComponent } from 'app/components/ui-kit/images/svg-loader/svg-loader.component';
import { SpinnerComponent } from "../../../ui-kit/loaders/spinner/spinner.component";
import { uniqueId } from 'app/utils/crypto/unique-ids';
import { holdPls } from 'app/utils/time/timing-utils';

@Component({
  selector: 'app-flight-map',
  standalone: true,
  imports: [CommonModule, SvgLoaderComponent, SpinnerComponent],
  templateUrl: './flight-map.component.html',
  styleUrl: './flight-map.component.scss'
})
export class FlightMapComponent {
  @Input() originLabel!: string;
  @Input() destinationLabel!: string;
  @Input() generalDestinationType: string = null;
  mapId = uniqueId('mapbox');
  map!: mapboxgl.Map;
  isLoaded: boolean = false;

  constructor(private locationService: LocationService) { }

  async ngAfterViewInit() {
    if (!this.generalDestinationType) {
      this.getCords();
    } else {
      await holdPls(250);
      this.isLoaded = true;
    }
  }

  async getCords() {
    const originCords = await this.locationService.getCoordinates(this.originLabel);
    const destinationCords = await this.locationService.getCoordinates(this.destinationLabel);

    if (originCords && destinationCords) {
      this.drawMap(originCords, destinationCords);
    }

  }

  drawMap(originCords, destinationCords) {
    mapboxgl.accessToken = environment.mapBoxToken;

    this.map = new mapboxgl.Map({
      container: this.mapId,
      style: 'mapbox://styles/p3ter185/cmcm3plwa00ae01s1f9ai1wbk',
      zoom: 0.5,
      dragPan: false
    });

    const origin: [number, number] = originCords;
    const destination: [number, number] = destinationCords;

    const line = greatCircle(origin, destination);
    this.map.on('load', () => {
      this.animateMap(origin, destination, line, originCords, destinationCords)
    });
  }

  async animateMap(origin, destination, line, originCords, destinationCords) {
    this.map.getStyle().layers?.forEach(layer => {
      if (layer.id.includes('road')) {
        this.map.setLayoutProperty(layer.id, 'visibility', 'none');
      }
    });
    this.map.addSource('route', {
      type: 'geojson',
      data: line
    });

    await holdPls(500);
    this.isLoaded = true;


    this.map.addLayer({
      id: 'route',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#000000',
        'line-width': 2,
        'line-opacity': 1,
        'line-dasharray': [1, 3],
      }
    });

    const bounds = new mapboxgl.LngLatBounds();

    bounds.extend(originCords);
    bounds.extend(destinationCords);

    this.map.jumpTo({
      center: destination,
      zoom: 5
    });

    setTimeout(() => {
      this.map.fitBounds(bounds, {
        padding: 16, 
        linear: true,
        duration: 1000
      });
    }, 1800);

    const endpointsGeoJSON: FeatureCollection<Point> = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: origin
          },
          properties: { label: 'Origin' }
        },
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: destination
          },
          properties: { label: 'Destination' }
        }
      ]
    };

    this.map.addSource('endpoints', {
      type: 'geojson',
      data: endpointsGeoJSON
    });

    this.map.addLayer({
      id: 'endpoints',
      type: 'circle',
      source: 'endpoints',
      paint: {
        'circle-radius': 2,
        'circle-color': '#000000',
        'circle-stroke-width': 1,
        'circle-stroke-color': '#000000'
      }
    });


    this.map.dragPan.disable();
    this.map.scrollZoom.disable();
    this.map.boxZoom.disable();
    this.map.keyboard.disable();
    this.map.doubleClickZoom.disable();
    this.map.touchZoomRotate.disable();

  };
}
